import { Component, ElementRef, EventEmitter, Output, ViewChild, OnDestroy, Input } from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import { MenuItem, ToastMessageOptions } from 'primeng/api';
import { MessageService } from '../../services/message-service';
import { SiteSubinventory } from '../../models/subinventory';
import {Site} from "../../models/site";
import { FileManagerService } from '../../services/file-manager.service';
import {finalize, tap} from "rxjs/operators";
import {BulkCreateResult} from "../../models/generic/bulk-create-result";
import {getSiteInfo, SiteInfo} from "../../helpers/site-info";

@Component({
    selector: 'file-import',
    templateUrl: './file-import.component.html',
    styleUrls: ['./file-import.component.scss'],
    standalone: false
})
export class FileImportComponent {

    UPLOADED_MESSAGE(fileType: string): ToastMessageOptions {
        return {
            severity: 'success',
            summary: 'File Imported',
            detail: `The ${fileType} was imported.`
        };
    };

    private _labels: string[] = [];

    @Input() set label(value: string | string[]) {
        this._labels = Array.isArray(value) ? value : [ value ];
        if (this._labels.length == 1) {
            this.selectedLabel = this._labels[0];
        }
    }

    selectedLabel: string;

    get labels(): string[] {
        return this._labels;
    }

    private _subinventories: SiteSubinventory[];
    @Input() set subinventories(val: SiteSubinventory[]) {
        if (val) {
            this._subinventories = val;
            this.items = val.map(s => ({
                label: s.name,
                icon: 'pi pi-upload',
                command: () => {
                    this.chooseFile(s);
                }
            }));
        }
    };
    get subinventories(): SiteSubinventory[] {
        return this._subinventories;
    }

    private _entityParent: string;
    @Input() set entityParent(val: string) {
        if (val) {
            this._entityParent = val;
        }
    }

    get entityParent(): string {
        return this._entityParent;
    }

    private _entityId: number;
    @Input() set entityId(val: number) {
        if (val) {
            this._entityId = val;
        }
    }

    get entityId(): number {
        return this._entityId;
    }

    private _siteId: string;
    @Input() set siteId(val: string) {
        if (val) {
            this._siteId = val;
        }
    }
    get siteId(): string {
        return this._siteId;
    }

    @Input() fileFormat: string;

    @Output() fileUploaded: EventEmitter<string> = new EventEmitter<string>();
    @Output() onSelectedLabelChange: EventEmitter<string> = new EventEmitter<string>();

    working: boolean = false;
    downloadingTemplate: boolean = false;

    @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;

    private _items: MenuItem[];
    get items(): MenuItem[] {
        if (!this._items)
            this._items = [{ label: 'Choose File', icon: 'pi pi-upload', command: () => this.chooseFile()}];
        return this._items;
    }

    set items(val) {
        if (!!val)
            this._items = val;
    }

    private readonly templateDownloads: Partial<Record<FileType, () => Observable<Blob>>> = {
        [FileType.walmartloads]: () => this.uploadService.DownloadWalmartTemplate(),
    };

    get hasTemplateDownload(): boolean {
        return this.selectedLabel in this.templateDownloads;
    }

    private selected: SiteSubinventory = null;

    constructor(
        private uploadService: FileManagerService,
        private messageService: MessageService) { }

    chooseFile(subinventory?: SiteSubinventory): void {
        this.selected = subinventory;

        this.fileInput.nativeElement.click();
    }

    getFile(): File {
        return this.fileInput.nativeElement.files[0];
    }

    startUpload(): void {
        this.working = true;
        const siteId = this.selected?.siteId || this.siteId;
        const file = this.getFile();
        const fileEnum = Object.entries(FileType).find(([key, value]) => value === this.selectedLabel);
        const [fileType] = fileEnum;
        if (file) {
            if (!this.entityId) {
                const storage = this.uploadService.UploadFile(fileType, file, siteId, this.selected?.id)

                storage.subscribe(s => {
                    if (Array.isArray(s)) {
                        this.handleBulkResults(s);
                    } else {
                        this.messageService.add(this.UPLOADED_MESSAGE(fileType));
                    }

                    this.fileInput.nativeElement.value = null;

                    this.fileUploaded.emit();

                    this.working = false;
                }, _err => {
                    this.fileInput.nativeElement.value = null;
                    this.working = false;
                });
            } else {
                this.blobStorageUpload(file, siteId, this.entityId, this.entityParent).subscribe();
            }
        }
    }

    blobStorageUpload(file?: File, siteId?: string, entityId?: number, entityParent?: string): Observable<string> {
        this.entityId = entityId;
        this.entityParent = entityParent;
        const fileEnum = Object.entries(FileType).find(([key, value]) => value === this.selectedLabel);
        const [fileType] = fileEnum;
        if (file) {
            const storage = this.uploadService.UploadBlob(siteId, fileType, file, this.entityParent, this.entityId);
            return storage.pipe(tap(fileId => {
                this.messageService.add(this.UPLOADED_MESSAGE(fileType));

                this.fileInput.nativeElement.value = null;

                this.fileUploaded.emit(fileId);

                this.working = false;
            }));
        } else
            return null;
    }

    private handleBulkResults(results: BulkCreateResult<unknown, unknown>[]): void {
        const errors = results.filter(r => r.error !== null);
        const successCount = results.length - errors.length;

        const message: ToastMessageOptions = {
            severity: errors.length > 0 ? 'error' : successCount > 0 ? 'success' : 'info',
            summary: `${successCount} load(s) successfully created`,
            sticky: true,
        };

        if (errors.length > 0) {
            message.detail = `${errors.length} load(s) not created due to errors:`;
            message.data = {
                list: errors.map(e => `Row ${e.key}: ${e.error}`)
            };
        }

        this.messageService.add(message);
    }

    downloadTemplate(): void {
        this.downloadingTemplate = true;
        this.templateDownloads[this.selectedLabel as FileType]?.().pipe(
            finalize(() => this.downloadingTemplate = false),
        ).subscribe();
    }

    isArray(obj: any): boolean {
        return Array.isArray(obj);
    }

    emitChange() {
        this.onSelectedLabelChange.emit(this.selectedLabel);
    }

    getFileFormats() {
        switch (this.fileFormat) {
            case 'spreadsheets':
                return '.csv,text/csv,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            case 'scanned':
                return '.pdf,application/pdf,.jpg,.jpeg,.tiff,.bmp,.gif,.png,image/*'
            default:
                return '*'
        }
    }
}

export enum FileType {
    cheploads = 'Chep Import',
    walmartloads = 'Walmart Import',
    pimsUpload = 'Comparison PIMS',
    towerUpload = 'Comparison Tower',
    materialsUpload = 'Comparison Materials',
    pecoUpload = 'Comparison PECO',
    laborUpload = 'Labor Upload',
    payrollUpload = 'Payroll GL Upload',
    uacPalletCount = 'Pallet Count Upload',
    uacReceivedPallets = 'Received Pallet Upload',
    uacShuttle = 'Shuttle Upload',
    uacBales = 'Bales Upload',
    receiptImport = 'Receipts Upload',
    outboundLoad = 'Outbound Load',
    inboundLoad = 'Inbound Load',
    plantEndOfShift = 'Plant End of Shift',
    tpmEndOfShift = 'TPM End of Shift',
    inventoryTickSheet = 'Inventory Tick',
    yardCheck = 'Yard Check',
    audits = 'Audits',
    pieceRate = 'Piece Rate',
    uacEndOfShift = 'UAC End of Shift',
    // Keep the typo'd key as an alias for backward compatibility with any existing data.
    uacEndOOfShift = 'UAC End of Shift',
    uacBols = 'Inbound BOLs',
    unloadSheets = 'Unload Worksheets',
    uacOutbound = 'Outbound Documents',
    trailerMovement = 'Trailer Movement',
    driverLogs = 'Driver Logs'
}

export function getWorkedShiftFileTypes(site?: Site): FileType[] {
    const siteInfo = getSiteInfo(site);

    if (siteInfo.isUAC) {
        return [
            FileType.uacEndOfShift,
            FileType.uacBols,
            FileType.unloadSheets,
            FileType.uacOutbound,
            FileType.trailerMovement,
            FileType.yardCheck,
            FileType.audits,
            FileType.driverLogs,
        ];
    }

    return [
        ...(siteInfo.hasTpm ? [FileType.tpmEndOfShift] : []),
        ...(siteInfo.hasPlant ? [FileType.plantEndOfShift] : []),
        FileType.inventoryTickSheet,
        FileType.pieceRate,
        FileType.yardCheck,
        FileType.audits,
        FileType.driverLogs,
    ];
}

export function getFileTypeLabel(fileType?: string): string | undefined {
    return fileType ? FileType[fileType as keyof typeof FileType] ?? fileType : undefined;
}
